'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { useStore } from 'zustand';

import type { AddToCartUx } from '@/api/variocube';
import { useCartItemStatus, useCartStore } from '@/store/cart';
import { storeUiModal } from '@/store-ui/modal';
import { toastCartAdded } from '@/ui/cart-toast-added';
import { UiKitDrawer } from '@/ui-kit/drawer';

import { ProductVariants } from './components';
import { styles } from './FeatureProductModalVariant.styles';
import { shouldShowSizeSelector } from './utils/shouldShowSizeSelector';

export function FeatureProductModalVariant() {
  const t = useTranslations();
  const slots = styles();

  const modal = useStore(storeUiModal, (state) => state.modals.productModalVariant);
  const close = useStore(storeUiModal, (state) => state.close);

  const addItem = useCartStore((state) => state.addItem);
  const setQuantity = useCartStore((state) => state.setQuantity);

  const product = modal?.data?.product;
  const selectedVariantId = modal?.data?.selectedVariantId ?? null;
  const ux: AddToCartUx = modal?.data?.ux ?? 'legacy';
  const isExperiment = ux === 'afterAdd' || ux === 'withBlock';

  const [isAddedInSession, setIsAddedInSession] = useState(false);
  const [pendingQuantity, setPendingQuantity] = useState(1);
  const cartStatus = useCartItemStatus(selectedVariantId);

  useEffect(() => {
    if (!modal?.isOpen) {
      setIsAddedInSession(false);
      setPendingQuantity(1);
    }
  }, [modal?.isOpen]);

  useEffect(() => {
    setIsAddedInSession(false);
    setPendingQuantity(1);
  }, [product?.uuid]);

  useEffect(() => {
    setPendingQuantity(1);
  }, [selectedVariantId]);

  // Без селектора размеров — сразу выбираем единственный оффер
  useEffect(() => {
    if (!product?.productOffers?.length) return;
    if (shouldShowSizeSelector(product.productOffers)) return;
    if (selectedVariantId) return;

    const [offer] = product.productOffers;
    if (!offer) return;

    storeUiModal.getState().patchData('productModalVariant', {
      product,
      selectedVariantId: offer.uuid
    });
  }, [product, selectedVariantId]);

  const selectedOfferStock =
    product?.productOffers?.find((offer) => offer.uuid === selectedVariantId)
      ?.totalStock ?? 1;

  const handleVariantClick = (variantId: string) => {
    if (!product) return;

    storeUiModal.getState().patchData('productModalVariant', {
      product,
      selectedVariantId: variantId
    });
  };

  const handleAddToCart = async () => {
    if (!selectedVariantId || !product?.uuid) return;

    const added = await addItem({
      offerUuid: selectedVariantId,
      productUuid: product.uuid,
      quantity: isExperiment ? pendingQuantity : 1,
      gtmPosition: modal?.data?.gtmPosition
    });

    if (!added) return;

    if (isExperiment) {
      setIsAddedInSession(true);
      return;
    }

    toastCartAdded({
      src: product.imageUrl,
      subtitle: product.name
    });
    close('productModalVariant');
  };

  const handleContinueShopping = async () => {
    if (!product) return;

    const isAdded = isAddedInSession || cartStatus.isInCart;

    if (!isAdded) {
      if (!selectedVariantId || !product.uuid) return;

      const added = await addItem({
        offerUuid: selectedVariantId,
        productUuid: product.uuid,
        quantity: pendingQuantity,
        gtmPosition: modal?.data?.gtmPosition
      });

      if (!added) return;
      setIsAddedInSession(true);
    }

    toastCartAdded({
      src: product.imageUrl,
      subtitle: product.name
    });
    close('productModalVariant');
  };

  const handleQuantityChange = async (quantity: number) => {
    if (isExperiment) {
      setPendingQuantity(quantity);
      return;
    }

    if (!selectedVariantId || !product?.uuid) return;

    await setQuantity({
      offerUuid: selectedVariantId,
      productUuid: product.uuid,
      quantity
    });
  };

  if (!modal?.isOpen) return null;

  if (!product || !product.productOffers || product.productOffers.length === 0)
    return null;

  const drawerTitle = isExperiment
    ? isAddedInSession || cartStatus.isInCart
      ? t('productVariantModal.titleAdded')
      : t('productVariantModal.titleAdd')
    : t('productVariantModal.title');

  return (
    <UiKitDrawer
      open
      onOpenChange={(isOpen) => {
        if (!isOpen) close('productModalVariant');
      }}
      title={drawerTitle}
      direction="right"
      className={isExperiment ? slots.drawerContainer() : undefined}
      contentClassName={isExperiment ? slots.drawerContent() : undefined}
      closeButtonClassName={isExperiment ? slots.closeButton() : undefined}
      dividerClassName={isExperiment ? slots.divider() : undefined}
    >
      <ProductVariants
        selectedVariantId={selectedVariantId}
        onVariantClick={handleVariantClick}
        onAddToCart={handleAddToCart}
        onContinueShopping={handleContinueShopping}
        onClose={() => close('productModalVariant')}
        product={product}
        isLoading={cartStatus.isLoading}
        isInCart={cartStatus.isInCart}
        cartQuantity={
          isExperiment ? pendingQuantity : (cartStatus.cartItem?.quantity ?? 1)
        }
        onQuantityChange={handleQuantityChange}
        maxOfferQuantity={
          isExperiment
            ? Math.max(selectedOfferStock, 1)
            : cartStatus.cartItem?.maxOfferQuantity
        }
        ux={ux}
        isAddedInSession={isAddedInSession}
      />
    </UiKitDrawer>
  );
}
